import React from 'react';
import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import { TypeProductFields } from '@/core/generated-types';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
} from '@/utils/rudderstack';
import { ExtendedProductFields } from '@/core/contentfulConfig';
import { SelectedAddOns } from '@/components/common/ProductAddToCart/ProductAddToCart';

export interface HandleCartFormSubmitProps {
  e: React.FormEvent<HTMLFormElement>;
  shopifyProduct: ShopifyProduct;
  shopifyVariant: ShopifyVariant;
  productFields: TypeProductFields;
  defaultQuantity: number;
  buttonLevel:
    | PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA
    | PRODUCT_ADDED_COMPONENT_TYPES.FULL_WIDTH_PRODUCT_CARD;
  url?: string;
  selectedAddOns?: SelectedAddOns[];
  onSubmitSuccess?: () => void;
  onSubmitFailure?: () => void;
  onStartFormSubmission?: () => void;
  noRedirect?: boolean;
}

export async function handleCartFormSubmit(props: HandleCartFormSubmitProps) {
  const {
    e,
    noRedirect,
    onSubmitSuccess,
    onSubmitFailure,
    onStartFormSubmission,
  } = props;

  onStartFormSubmission?.();

  try {
    e.preventDefault();
    captureAddToCartEvents(props);
  } catch (error) {
    console.error('Error encountered tracking add to cart events', error);
  }

  if (noRedirect) {
    const formData = new FormData(e.currentTarget);

    const addToCartResponse = await fetch('/api/cart/add?noredirect=1', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.fromEntries(formData.entries())),
    });

    if (addToCartResponse.ok) {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } else {
      console.error(
        'Error encountered submitting cart',
        addToCartResponse.json(),
      );

      if (onSubmitFailure) {
        onSubmitFailure();
      }
    }
  } else {
    e.currentTarget.submit();
  }
}

function captureAddToCartEvents({
  e,
  shopifyProduct,
  shopifyVariant,
  defaultQuantity,
  buttonLevel,
  productFields,
  url,
  selectedAddOns,
}: HandleCartFormSubmitProps) {
  const formData = new FormData(e.currentTarget);
  const qtyVal = Number(formData.get('quantity') || defaultQuantity);
  const urlOrFallback = url || '';

  pushEvent('addToCart', { qtyVal, shopifyProduct, variant: shopifyVariant });

  captureProductFieldsEvent(
    PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
    {
      shopifyProduct,
      variant: shopifyVariant,
      product: productFields,
      quantity: qtyVal,
      url: urlOrFallback,
    },
    buttonLevel,
  );

  selectedAddOns &&
    selectedAddOns?.forEach((selectedAddOn) => {
      const shopifyProductAddOn = (
        selectedAddOn?.fields as ExtendedProductFields
      )?.shopifyProduct;

      if (shopifyProductAddOn) {
        pushEvent('addToCart', {
          qtyVal: 1,
          shopifyProduct: shopifyProductAddOn as ShopifyProduct,
          variant: selectedAddOn.variant,
        });

        captureProductFieldsEvent(
          PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
          {
            shopifyProduct: shopifyProductAddOn as ShopifyProduct,
            variant: selectedAddOn.variant,
            product: productFields,
            quantity: 1,
            url: urlOrFallback,
          },
          PRODUCT_ADDED_COMPONENT_TYPES.UPSELL_PDP,
        );
      }
    });
}
